import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import styled from 'styled-components';

import { useAccountAdminInformation } from '../../../views/account/admin/common/hook/query/useAccountAdminInformation';
import useEditAdminAccount from '../../../views/account/admin/common/hook/query/useEditAdminAccount';

import PageTemplate from '../../../components/template/PageTemplate';
import AdminFormNavbar from '../../../views/account/admin/form/AdminFormNavbar';
import AccountAdminEditForm from '../../../views/account/admin/form/AccountAdminEditForm/AccountAdminEditForm';

import {
  ACCOUNT_ADMIN_PAGE_NAME,
  ACCOUNT_ADMIN_TAB_KEY,
  ACCOUNT_ADMIN_TAB_TITLE,
} from '../../../constants/service/app/accountAdminConstants';
import { ADMIN_LEVEL_TYPE, FORM_DEFAULT_VALUE } from '../../../views/account/admin/common/constants';
import {
  AccountAdminEditRequest,
  AdminInfoType,
  FormMode,
} from '../../../views/account/admin/common/types/AccountAdminFormType';
import { editFormValidationSchema } from '../../../views/account/admin/common/util/formValidationSchema';
import toastMsg from '../../../lib/toastMsg';
import { useAuth } from '../../../hooks/common/useAuth';
import PermissionTable from '../../../views/account/admin/list/PermissionTable';
import Tabs from '../../../components/Tabs/Tabs';

const Form = styled.form`
  width: 100%;
`;

type RouteParams = {
  id?: string;
};

const AccountAdminEdit = (): React.JSX.Element => {
  const { id } = useParams<RouteParams>();
  const adminId = Number(id);
  const hasValidId = !!id && Number.isFinite(adminId);
  const { user } = useAuth();

  const isMasterAccount = user?.level === ADMIN_LEVEL_TYPE.MASTER;
  const isMyAccount = hasValidId && adminId === user?.id;

  const methods = useForm<AccountAdminEditRequest>({
    mode: 'onChange',
    // TODO: npm => pnpm 의존성 변경 이후 타입 불일치가 감지돼 임시로 any 사용 수정 필요
    resolver: yupResolver(editFormValidationSchema) as any,
    defaultValues: FORM_DEFAULT_VALUE,
    resetOptions: {
      // keep dirty fields unchanged, but update defaultValues
      keepDirtyValues: true,
    },
  });

  const [currentTabKey, setCurrentTabKey] = useState(ACCOUNT_ADMIN_TAB_KEY.ADMIN_INFO);

  const { data: adminInfo } = useAccountAdminInformation({
    id: adminId,
    options: {
      enabled: hasValidId,
      onSuccess: (data: { data: AdminInfoType }) => {
        methods.reset(data?.data);
      },
      onError: (error) => toastMsg.error(error.message),
    },
  });

  const { mutate: editAdminAccount, isLoading } = useEditAdminAccount();

  const onSubmit = (data: AccountAdminEditRequest) => {
    if (!hasValidId) {
      toastMsg.error('잘못된 접근입니다. 관리자 ID를 확인해주세요.');
      return;
    }
    const { level, status, name, password, permission } = data;
    const formAttachment: AccountAdminEditRequest = {
      name,
      password,
      permission,
    };
    if (isLoading) {
      return;
    }
    if (isMasterAccount) {
      formAttachment.level = level;
      formAttachment.status = status;
    }
    editAdminAccount({ id: adminId, data: formAttachment });
  };

  const onError = () => {
    setCurrentTabKey(ACCOUNT_ADMIN_TAB_KEY.ADMIN_INFO);
  };

  return (
    <PageTemplate pageName={ACCOUNT_ADMIN_PAGE_NAME.ADMIN_EDIT}>
      <FormProvider {...methods}>
        <Form id="account-admin-form" onSubmit={methods.handleSubmit(onSubmit, onError)}>
          {(isMyAccount || isMasterAccount) && <AdminFormNavbar mode={FormMode.EDIT} />}
          <Tabs
            className="admin-create-tabs"
            onChange={(tabKey: number) => setCurrentTabKey(tabKey)}
            activeKey={currentTabKey}
            list={[
              {
                title: ACCOUNT_ADMIN_TAB_TITLE.ADMIN_INFO,
                content: <AccountAdminEditForm isMasterAccount={isMasterAccount} />,
              },
              {
                title: ACCOUNT_ADMIN_TAB_TITLE.ADMIN_PERMISSION,
                content: <PermissionTable mode={FormMode.EDIT} adminInfo={adminInfo} />,
                disabled: !isMasterAccount,
              },
            ]}
          />
        </Form>
      </FormProvider>
    </PageTemplate>
  );
};

export default AccountAdminEdit;
